import type {
  FeatureDto,
  BuildDto,
  CodeDirectoryDto,
  CodeFileDto,
  CoverageFileDto,
  CoverageRunDto,
  CoverageRunSummaryDto,
  JobListItemDto,
  PipelineDetailDto,
  PipelineListItemDto,
  PipelineRunSummaryDto,
  PipelineStageExecutionListItemDto,
  PipelineStageListItemDto,
  UploadDto,
  UploadTokenDto,
  ExecutionRunDto,
  OrganisationDto,
  OrganisationEntitlementsDto,
  OrganisationPlanUpgradePreviewDto,
  ProjectDto,
  ReleaseDetailDto,
  ReleaseListItemDto,
  ResultsFileDto,
  SuiteDetailDto,
  SuiteListItemDto,
  SuiteRunDto,
  TestDetailDto,
  TestListItemDto,
  TestRunDto,
  ProjectCsvMappingDto,
} from '@flowqa/shared';
import type { QueryResult } from '@flowqa/shared/query';

type FeatureDtoWire = Omit<FeatureDto, 'plannedFor' | 'releasedAt' | 'deletedAt'> & {
  plannedFor?: Date | string | null;
  releasedAt?: Date | string | null;
  deletedAt: Date | string | null;
};

type ReleaseListItemDtoWire = Omit<
  ReleaseListItemDto,
  'plannedFor' | 'releasedAt' | 'deletedAt'
> & {
  plannedFor: Date | string | null;
  releasedAt: Date | string | null;
  deletedAt: Date | string | null;
};

type ReleaseDetailDtoWire = Omit<ReleaseDetailDto, 'plannedFor' | 'releasedAt' | 'deletedAt'> & {
  plannedFor: Date | string | null;
  releasedAt: Date | string | null;
  deletedAt: Date | string | null;
};

type SuiteRunDtoWire = Omit<SuiteRunDto, 'timestamp' | 'createdAt' | 'deletedAt'> & {
  timestamp: Date | string;
  createdAt: Date | string;
  deletedAt: Date | string | null;
};

type ExecutionRunDtoWire = Omit<
  ExecutionRunDto,
  'startedAt' | 'finishedAt' | 'resultsFileCreatedAt' | 'deletedAt'
> & {
  startedAt: Date | string | null;
  finishedAt: Date | string | null;
  resultsFileCreatedAt?: Date | string | null;
  deletedAt: Date | string | null;
};

type CoverageRunDtoWire = Omit<
  CoverageRunDto,
  'capturedAt' | 'bucketDate' | 'createdAt' | 'updatedAt' | 'deletedAt'
> & {
  capturedAt?: Date | string | null;
  bucketDate?: Date | string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
  deletedAt: Date | string | null;
};

type CoverageRunSummaryDtoWire = Omit<CoverageRunSummaryDto, 'latestCapturedAt'> & {
  latestCapturedAt: Date | string | null;
};

type CoverageFileDtoWire = Omit<
  CoverageFileDto,
  'capturedAt' | 'createdAt' | 'updatedAt' | 'deletedAt'
> & {
  capturedAt?: Date | string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
  deletedAt: Date | string | null;
};

type CodeFileDtoWire = Omit<
  CodeFileDto,
  'latestCapturedAt' | 'createdAt' | 'updatedAt' | 'deletedAt'
> & {
  latestCapturedAt?: Date | string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
  deletedAt: Date | string | null;
};

type CodeDirectoryDtoWire = Omit<CodeDirectoryDto, 'createdAt' | 'updatedAt' | 'deletedAt'> & {
  latestCapturedAt?: Date | string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
  deletedAt: Date | string | null;
};

type ProjectCsvMappingDtoWire = Omit<ProjectCsvMappingDto, 'createdAt' | 'updatedAt'> & {
  createdAt: Date | string;
  updatedAt: Date | string;
};

type OrganisationDtoWire = Omit<OrganisationDto, 'createdAt' | 'subscription'> & {
  createdAt: Date | string;
  subscription: Omit<
    OrganisationDto['subscription'],
    | 'trialEndsAt'
    | 'planEndsAt'
    | 'renewalCanceledAt'
    | 'scheduledPlanEffectiveAt'
    | 'billingDelinquentSinceAt'
    | 'billingNextPaymentAttemptAt'
    | 'billingUploadGraceEndsAt'
  > & {
    trialEndsAt: Date | string | null;
    planEndsAt: Date | string | null;
    renewalCanceledAt: Date | string | null;
    scheduledPlanEffectiveAt: Date | string | null;
    billingDelinquentSinceAt: Date | string | null;
    billingNextPaymentAttemptAt: Date | string | null;
    billingUploadGraceEndsAt: Date | string | null;
  };
};

type OrganisationEntitlementsDtoWire = Omit<
  OrganisationEntitlementsDto,
  'subscription' | 'usage' | 'access' | 'trialEndsAt' | 'planEndsAt'
> & {
  subscription: Omit<
    OrganisationEntitlementsDto['subscription'],
    | 'trialEndsAt'
    | 'planEndsAt'
    | 'renewalCanceledAt'
    | 'scheduledPlanEffectiveAt'
    | 'billingDelinquentSinceAt'
    | 'billingNextPaymentAttemptAt'
    | 'billingUploadGraceEndsAt'
  > & {
    trialEndsAt: Date | string | null;
    planEndsAt: Date | string | null;
    renewalCanceledAt: Date | string | null;
    scheduledPlanEffectiveAt: Date | string | null;
    billingDelinquentSinceAt: Date | string | null;
    billingNextPaymentAttemptAt: Date | string | null;
    billingUploadGraceEndsAt: Date | string | null;
  };
  usage: Omit<OrganisationEntitlementsDto['usage'], 'periodStart' | 'periodEnd'> & {
    periodStart: Date | string;
    periodEnd: Date | string;
    executionDailyBreakdown: Array<
      Omit<OrganisationEntitlementsDto['usage']['executionDailyBreakdown'][number], 'day'> & {
        day: Date | string;
      }
    >;
  };
  access: Omit<OrganisationEntitlementsDto['access'], 'memberGraceEndsAt'> & {
    memberGraceEndsAt: Date | string | null;
  };
  trialEndsAt: Date | string | null;
  planEndsAt: Date | string | null;
};

type OrganisationPlanUpgradePreviewDtoWire = Omit<
  OrganisationPlanUpgradePreviewDto,
  'nextSubscription' | 'billingPreview' | 'impacts'
> & {
  nextSubscription: Omit<
    OrganisationPlanUpgradePreviewDto['nextSubscription'],
    | 'trialEndsAt'
    | 'planEndsAt'
    | 'renewalCanceledAt'
    | 'scheduledPlanEffectiveAt'
    | 'billingDelinquentSinceAt'
    | 'billingNextPaymentAttemptAt'
    | 'billingUploadGraceEndsAt'
  > & {
    trialEndsAt: Date | string | null;
    planEndsAt: Date | string | null;
    renewalCanceledAt: Date | string | null;
    scheduledPlanEffectiveAt: Date | string | null;
    billingDelinquentSinceAt: Date | string | null;
    billingNextPaymentAttemptAt: Date | string | null;
    billingUploadGraceEndsAt: Date | string | null;
  };
  billingPreview: Omit<OrganisationPlanUpgradePreviewDto['billingPreview'], 'nextBillingDate'> & {
    nextBillingDate: Date | string | null;
  };
  impacts: Omit<OrganisationPlanUpgradePreviewDto['impacts'], 'memberGraceEndsAt'> & {
    memberGraceEndsAt: Date | string | null;
  };
};

type BuildDtoWire = Omit<BuildDto, 'createdAt' | 'startedAt' | 'finishedAt' | 'deletedAt'> & {
  createdAt: Date | string;
  startedAt: Date | string | null;
  finishedAt: Date | string | null;
  deletedAt: Date | string | null;
  stageSummaries?: Array<
    Omit<NonNullable<BuildDto['stageSummaries']>[number], 'timestamp'> & {
      timestamp: Date | string | null;
    }
  >;
};

type JobListItemDtoWire = Omit<
  JobListItemDto,
  'lastUpdatedAt' | 'deletedAt' | 'latestRun' | 'runSummaries'
> & {
  lastUpdatedAt: Date | string | null;
  deletedAt: Date | string | null;
  latestRun?:
    | (Omit<NonNullable<JobListItemDto['latestRun']>, 'timestamp'> & {
        timestamp: Date | string | null;
      })
    | null;
  runSummaries?: Array<
    Omit<NonNullable<JobListItemDto['runSummaries']>[number], 'timestamp'> & {
      timestamp: Date | string | null;
    }
  >;
};

type PipelineStageListItemDtoWire = Omit<
  PipelineStageListItemDto,
  'lastUpdatedAt' | 'deletedAt' | 'latestRun' | 'executionSummaries'
> & {
  lastUpdatedAt: Date | string | null;
  deletedAt: Date | string | null;
  latestRun?:
    | (Omit<NonNullable<PipelineStageListItemDto['latestRun']>, 'timestamp'> & {
        timestamp: Date | string | null;
      })
    | null;
  executionSummaries?: Array<
    Omit<NonNullable<PipelineStageListItemDto['executionSummaries']>[number], 'timestamp'> & {
      timestamp: Date | string | null;
    }
  >;
};

type PipelineStageExecutionListItemDtoWire = Omit<
  PipelineStageExecutionListItemDto,
  'startedAt' | 'finishedAt' | 'lastUpdatedAt' | 'deletedAt'
> & {
  startedAt: Date | string | null;
  finishedAt: Date | string | null;
  lastUpdatedAt: Date | string | null;
  deletedAt: Date | string | null;
};

const coerceDate = (value: Date | string | null | undefined): Date | null => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) ? null : parsed;
};

const coerceRequiredDate = (value: Date | string): Date => {
  if (value instanceof Date) return value;
  return new Date(value);
};

export const mapQueryResult = <TInput, TOutput>(
  result: QueryResult<TInput>,
  mapItem: (item: TInput) => TOutput,
): QueryResult<TOutput> => ({ ...result, items: result.items.map(mapItem) });

export const mapOrganisationDto = (dto: OrganisationDtoWire): OrganisationDto => ({
  ...dto,
  createdAt: coerceRequiredDate(dto.createdAt),
  subscription: {
    ...dto.subscription,
    trialEndsAt: coerceDate(dto.subscription.trialEndsAt),
    planEndsAt: coerceDate(dto.subscription.planEndsAt),
    renewalCanceledAt: coerceDate(dto.subscription.renewalCanceledAt),
    scheduledPlanEffectiveAt: coerceDate(dto.subscription.scheduledPlanEffectiveAt),
    billingDelinquentSinceAt: coerceDate(dto.subscription.billingDelinquentSinceAt),
    billingNextPaymentAttemptAt: coerceDate(dto.subscription.billingNextPaymentAttemptAt),
    billingUploadGraceEndsAt: coerceDate(dto.subscription.billingUploadGraceEndsAt),
  },
});

export const mapOrganisationEntitlementsDto = (
  dto: OrganisationEntitlementsDtoWire,
): OrganisationEntitlementsDto => ({
  ...dto,
  subscription: {
    ...dto.subscription,
    trialEndsAt: coerceDate(dto.subscription.trialEndsAt),
    planEndsAt: coerceDate(dto.subscription.planEndsAt),
    renewalCanceledAt: coerceDate(dto.subscription.renewalCanceledAt),
    scheduledPlanEffectiveAt: coerceDate(dto.subscription.scheduledPlanEffectiveAt),
    billingDelinquentSinceAt: coerceDate(dto.subscription.billingDelinquentSinceAt),
    billingNextPaymentAttemptAt: coerceDate(dto.subscription.billingNextPaymentAttemptAt),
    billingUploadGraceEndsAt: coerceDate(dto.subscription.billingUploadGraceEndsAt),
  },
  usage: {
    ...dto.usage,
    periodStart: coerceRequiredDate(dto.usage.periodStart),
    periodEnd: coerceRequiredDate(dto.usage.periodEnd),
    executionDailyBreakdown: dto.usage.executionDailyBreakdown.map((entry) => ({
      ...entry,
      day: coerceRequiredDate(entry.day),
    })),
  },
  access: {
    ...dto.access,
    memberGraceEndsAt: coerceDate(dto.access.memberGraceEndsAt),
  },
  trialEndsAt: coerceDate(dto.trialEndsAt),
  planEndsAt: coerceDate(dto.planEndsAt),
});

export const mapOrganisationPlanUpgradePreviewDto = (
  dto: OrganisationPlanUpgradePreviewDtoWire,
): OrganisationPlanUpgradePreviewDto => ({
  ...dto,
  nextSubscription: {
    ...dto.nextSubscription,
    trialEndsAt: coerceDate(dto.nextSubscription.trialEndsAt),
    planEndsAt: coerceDate(dto.nextSubscription.planEndsAt),
    renewalCanceledAt: coerceDate(dto.nextSubscription.renewalCanceledAt),
    scheduledPlanEffectiveAt: coerceDate(dto.nextSubscription.scheduledPlanEffectiveAt),
    billingDelinquentSinceAt: coerceDate(dto.nextSubscription.billingDelinquentSinceAt),
    billingNextPaymentAttemptAt: coerceDate(dto.nextSubscription.billingNextPaymentAttemptAt),
    billingUploadGraceEndsAt: coerceDate(dto.nextSubscription.billingUploadGraceEndsAt),
  },
  billingPreview: {
    ...dto.billingPreview,
    nextBillingDate: coerceDate(dto.billingPreview.nextBillingDate),
  },
  impacts: {
    ...dto.impacts,
    memberGraceEndsAt: coerceDate(dto.impacts.memberGraceEndsAt),
  },
});

export const mapProjectDto = (dto: ProjectDto): ProjectDto => ({
  ...dto,
  createdAt: coerceRequiredDate(dto.createdAt),
  deletedAt: coerceDate(dto.deletedAt),
  projectApiKeyCreatedAt: coerceDate(dto.projectApiKeyCreatedAt),
  projectApiKeyLastUsedAt: coerceDate(dto.projectApiKeyLastUsedAt),
});

export const mapProjectCsvMappingDto = (dto: ProjectCsvMappingDtoWire): ProjectCsvMappingDto => ({
  ...dto,
  createdAt: coerceRequiredDate(dto.createdAt),
  updatedAt: coerceRequiredDate(dto.updatedAt),
});

export const mapExecutionRunDto = (dto: ExecutionRunDtoWire): ExecutionRunDto => ({
  ...dto,
  startedAt: coerceDate(dto.startedAt),
  finishedAt: coerceDate(dto.finishedAt),
  resultsFileCreatedAt: coerceDate(dto.resultsFileCreatedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapCoverageRunDto = (dto: CoverageRunDtoWire): CoverageRunDto => ({
  ...dto,
  capturedAt: coerceDate(dto.capturedAt),
  bucketDate: coerceDate(dto.bucketDate),
  createdAt: coerceRequiredDate(dto.createdAt),
  updatedAt: coerceRequiredDate(dto.updatedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapCoverageRunSummaryDto = (
  dto: CoverageRunSummaryDtoWire,
): CoverageRunSummaryDto => ({
  ...dto,
  latestCapturedAt: coerceDate(dto.latestCapturedAt),
});

export const mapCoverageFileDto = (dto: CoverageFileDtoWire): CoverageFileDto => ({
  ...dto,
  capturedAt: coerceDate(dto.capturedAt),
  createdAt: coerceRequiredDate(dto.createdAt),
  updatedAt: coerceRequiredDate(dto.updatedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapCodeFileDto = (dto: CodeFileDtoWire): CodeFileDto => ({
  ...dto,
  latestCapturedAt: coerceDate(dto.latestCapturedAt),
  createdAt: coerceRequiredDate(dto.createdAt),
  updatedAt: coerceRequiredDate(dto.updatedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapCodeDirectoryDto = (dto: CodeDirectoryDtoWire): CodeDirectoryDto => ({
  ...dto,
  latestCapturedAt: coerceDate(dto.latestCapturedAt),
  createdAt: coerceRequiredDate(dto.createdAt),
  updatedAt: coerceRequiredDate(dto.updatedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapFeatureDto = (dto: FeatureDtoWire): FeatureDto => ({
  ...dto,
  plannedFor: coerceDate(dto.plannedFor),
  releasedAt: coerceDate(dto.releasedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapReleaseListItemDto = (dto: ReleaseListItemDtoWire): ReleaseListItemDto => ({
  ...dto,
  plannedFor: coerceDate(dto.plannedFor),
  releasedAt: coerceDate(dto.releasedAt),
  features: dto.features
    ? {
        ...dto.features,
        items: dto.features.items?.map((feature) => ({
          ...feature,
          plannedFor: coerceDate(feature.plannedFor),
          releasedAt: coerceDate(feature.releasedAt),
          deletedAt: coerceDate(feature.deletedAt),
        })),
      }
    : dto.features,
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapReleaseDetailDto = (dto: ReleaseDetailDtoWire): ReleaseDetailDto => ({
  ...dto,
  plannedFor: coerceDate(dto.plannedFor),
  releasedAt: coerceDate(dto.releasedAt),
  features: dto.features
    ? {
        ...dto.features,
        items: dto.features.items?.map((feature) => ({
          ...feature,
          plannedFor: coerceDate(feature.plannedFor),
          releasedAt: coerceDate(feature.releasedAt),
          deletedAt: coerceDate(feature.deletedAt),
        })),
      }
    : dto.features,
  deletedAt: coerceDate(dto.deletedAt),
});

const coerceSuiteLatestRun = (
  latestRun: SuiteListItemDto['latestRun'],
): SuiteListItemDto['latestRun'] => {
  if (!latestRun) return latestRun;
  return {
    ...latestRun,
    timestamp: coerceRequiredDate(latestRun.timestamp),
  };
};

const coerceDailyHistory = <
  TItem extends { day: Date | string; details?: Array<{ timestamp: Date | string }> },
>(
  dailyHistory: TItem[] | undefined,
): Array<Omit<TItem, 'day'> & { day: Date }> | undefined =>
  dailyHistory?.map((item) => ({
    ...item,
    day: coerceRequiredDate(item.day),
    details: Array.isArray(item.details)
      ? item.details.map((detail) => ({
          ...detail,
          timestamp: coerceRequiredDate(detail.timestamp),
        }))
      : undefined,
  }));

export const mapSuiteListItemDto = (dto: SuiteListItemDto): SuiteListItemDto => ({
  ...dto,
  latestRun: coerceSuiteLatestRun(dto.latestRun),
  dailyHistory: coerceDailyHistory(dto.dailyHistory),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapSuiteDetailDto = (dto: SuiteDetailDto): SuiteDetailDto => ({
  ...dto,
  latestRun: coerceSuiteLatestRun(dto.latestRun),
  deletedAt: coerceDate(dto.deletedAt),
});

const coerceTestLatestRun = (
  latestRun: TestListItemDto['latestRun'],
): TestListItemDto['latestRun'] => {
  if (!latestRun) return latestRun;
  return {
    ...latestRun,
    timestamp: coerceRequiredDate(latestRun.timestamp),
  };
};

export const mapTestListItemDto = (dto: TestListItemDto): TestListItemDto => ({
  ...dto,
  latestRun: coerceTestLatestRun(dto.latestRun),
  dailyHistory: coerceDailyHistory(dto.dailyHistory),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapTestDetailDto = (dto: TestDetailDto): TestDetailDto => ({
  ...dto,
  latestRun: coerceTestLatestRun(dto.latestRun),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapSuiteRunDto = (dto: SuiteRunDtoWire): SuiteRunDto => ({
  ...dto,
  timestamp: coerceRequiredDate(dto.timestamp),
  createdAt: coerceRequiredDate(dto.createdAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapTestRunDto = (dto: TestRunDto): TestRunDto => ({
  ...dto,
  timestamp: coerceRequiredDate(dto.timestamp),
  createdAt: coerceRequiredDate(dto.createdAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapBuildDto = (dto: BuildDtoWire): BuildDto => ({
  ...dto,
  stageSummaries: dto.stageSummaries?.map((stage) => ({
    ...stage,
    timestamp: coerceDate(stage.timestamp),
  })),
  createdAt: coerceRequiredDate(dto.createdAt),
  startedAt: coerceDate(dto.startedAt),
  finishedAt: coerceDate(dto.finishedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapJobListItemDto = (dto: JobListItemDtoWire): JobListItemDto => ({
  ...dto,
  latestRun: dto.latestRun
    ? {
        ...dto.latestRun,
        timestamp: coerceDate(dto.latestRun.timestamp),
      }
    : dto.latestRun,
  runSummaries: dto.runSummaries?.map((run) => ({
    ...run,
    timestamp: coerceDate(run.timestamp),
  })),
  lastUpdatedAt: coerceDate(dto.lastUpdatedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

const coercePipelineLatestRun = (
  latestRun: PipelineListItemDto['latestRun'],
): PipelineListItemDto['latestRun'] => {
  if (!latestRun) return latestRun;
  return {
    ...latestRun,
    timestamp: coerceRequiredDate(latestRun.timestamp),
  };
};

export const mapPipelineListItemDto = (dto: PipelineListItemDto): PipelineListItemDto => ({
  ...dto,
  latestRun: coercePipelineLatestRun(dto.latestRun),
  runSummaries: dto.runSummaries,
  latestJobSummaries: dto.latestJobSummaries,
  stageSummaries: dto.stageSummaries,
  dailyHistory: coerceDailyHistory(dto.dailyHistory),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapPipelineDetailDto = (dto: PipelineDetailDto): PipelineDetailDto => ({
  ...dto,
  latestRun: coercePipelineLatestRun(dto.latestRun),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapPipelineStageListItemDto = (
  dto: PipelineStageListItemDtoWire,
): PipelineStageListItemDto => ({
  ...dto,
  latestRun: dto.latestRun
    ? {
        ...dto.latestRun,
        timestamp: coerceDate(dto.latestRun.timestamp),
      }
    : null,
  executionSummaries: dto.executionSummaries?.map((summary) => ({
    ...summary,
    timestamp: coerceDate(summary.timestamp),
  })),
  lastUpdatedAt: coerceDate(dto.lastUpdatedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapPipelineStageExecutionListItemDto = (
  dto: PipelineStageExecutionListItemDtoWire,
): PipelineStageExecutionListItemDto => ({
  ...dto,
  jobSummaries: dto.jobSummaries?.map((summary) => ({
    ...summary,
    timestamp: coerceDate(summary.timestamp),
  })),
  startedAt: coerceDate(dto.startedAt),
  finishedAt: coerceDate(dto.finishedAt),
  lastUpdatedAt: coerceDate(dto.lastUpdatedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapPipelineRunSummaryDto = (dto: PipelineRunSummaryDto): PipelineRunSummaryDto => ({
  ...dto,
  buckets: dto.buckets.map((bucket) => ({
    ...bucket,
    day: coerceRequiredDate(bucket.day),
  })),
  runSummaries: dto.runSummaries.map((run) => ({
    ...run,
    timestamp: coerceDate(run.timestamp),
  })),
});

export const mapUploadDto = (dto: UploadDto): UploadDto => ({
  ...dto,
  capturedAt: coerceDate(dto.capturedAt),
  createdAt: coerceRequiredDate(dto.createdAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapUploadTokenDto = (dto: UploadTokenDto): UploadTokenDto => ({
  ...dto,
  createdAt: coerceDate(dto.createdAt),
  lastUsedAt: coerceDate(dto.lastUsedAt),
  expiresAt: coerceDate(dto.expiresAt),
  revokedAt: coerceDate(dto.revokedAt),
});

export const mapResultsFileDto = (dto: ResultsFileDto): ResultsFileDto => ({
  ...dto,
  timestamp: coerceDate(dto.timestamp),
  createdAt: coerceRequiredDate(dto.createdAt),
  updatedAt: coerceRequiredDate(dto.updatedAt),
  deletedAt: coerceDate(dto.deletedAt),
  executionRunSummaries: dto.executionRunSummaries?.map((summary) => ({
    ...summary,
    timestamp: coerceDate(summary.timestamp),
  })),
  suiteRunSummaries: dto.suiteRunSummaries?.map((summary) => ({
    ...summary,
    timestamp: coerceDate(summary.timestamp),
  })),
  testRunSummaries: dto.testRunSummaries?.map((summary) => ({
    ...summary,
    timestamp: coerceDate(summary.timestamp),
  })),
});
